import * as React from 'react';
import { useMemo } from 'react';

import type { GridConfig } from '@coursera-web/cds-labs';

import type {
  CarouselFilter as CarouselFilterTrackingData,
  PageConfigComponent as PageConfigComponentTrackingData,
} from '@coursera/event-pulse-types';

import { getAdditionalComponentsHashMap } from 'bundles/collections-carousel/components/carousel/CollectionCarouselsContainer';
import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
// eslint-disable-next-line import/no-cycle
import ProductCardCollection from 'bundles/collections-common/components/ProductCardCollection';
import ProductCardCollectionPlaceholder from 'bundles/collections-common/components/ProductCardCollectionPlaceholder';

export type Props = {
  collections: CarouselCollection[];
  isCollectionsLoading?: boolean;
  additionalComponents?: ({ component: React.ReactNode; indexToInsert: number } | null)[];
  itemsGridConfig?: GridConfig;
  isLegacyCollection?: boolean; // leaving this here but should be deleted after migration
  customResponsiveProperties?: {
    breakpoint: number;
    settings: { slidesToShow: number; slidesToScroll: number; infinite: boolean };
  }[]; // leaving this here but should be deleted after migration
  hideLoadingTitle?: boolean;
  showAllEntries?: boolean;
  renderTitle?: () => React.ReactNode;
  productCardTrackingName?: string | null;
  isSearchMerchandisingCollection?: boolean;
  eventingData?: {
    searchQuery?: string;
    page?: string;
    filtersApplied?: Record<string, string[]>;
    searchIndex?: string;
    searchIndexPosition?: number;
    pageConfigComponent?: PageConfigComponentTrackingData;
    carouselFilter?: CarouselFilterTrackingData;
  };
  cardSpacing?: Record<string, unknown>;
  /** Force the list-view card variant regardless of viewport. Passed through
   * to ProductCardCollection. */
  forceListView?: boolean;
};
export const CollectionsContainer = ({
  collections,
  isCollectionsLoading,
  additionalComponents,
  itemsGridConfig,
  hideLoadingTitle = false,
  showAllEntries = false,
  renderTitle,
  productCardTrackingName,
  isSearchMerchandisingCollection,
  eventingData,
  cardSpacing,
  forceListView,
}: Props) => {
  const collectionsLength = collections.length;

  const additionalComponentsHashMap = useMemo(
    () => getAdditionalComponentsHashMap(collectionsLength, additionalComponents),
    [additionalComponents, collectionsLength]
  );

  if (!isCollectionsLoading && (!collections || collections.length === 0)) return null;

  return (
    <div className="rc-CollectionsContainer">
      {isCollectionsLoading ? (
        <ProductCardCollectionPlaceholder
          hideTitle={hideLoadingTitle}
          itemsGridConfig={itemsGridConfig}
          hideButton={showAllEntries}
        />
      ) : (
        collections.map((collection, index) => {
          const { recommenderConfigurationHash, context } = collection.entities?.[0] || {};
          const collectionRecommender = recommenderConfigurationHash && {
            hash: recommenderConfigurationHash,
            ...(context && { context }),
          }; // avoid sending null values
          const extraComponents = additionalComponentsHashMap.get(index);
          if (extraComponents) {
            return (
              <React.Fragment key={collection.collectionId}>
                {extraComponents}
                <ProductCardCollection
                  eventingData={eventingData}
                  productCardTrackingName={productCardTrackingName}
                  collection={collection}
                  key={collection.collectionId}
                  itemsGridConfig={itemsGridConfig}
                  showAllEntries={showAllEntries}
                  renderTitle={renderTitle}
                  cardSpacing={cardSpacing}
                  forceListView={forceListView}
                  {...(collectionRecommender && { collectionRecommender })}
                />
              </React.Fragment>
            );
          }

          return (
            <ProductCardCollection
              eventingData={eventingData}
              productCardTrackingName={productCardTrackingName}
              collection={collection}
              key={collection.collectionId}
              itemsGridConfig={itemsGridConfig}
              showAllEntries={showAllEntries}
              renderTitle={renderTitle}
              cardSpacing={cardSpacing}
              forceListView={forceListView}
              {...(collectionRecommender && { collectionRecommender })}
            />
          );
        })
      )}
    </div>
  );
};

export default CollectionsContainer;
